'use client';

import { c as _c } from "react/compiler-runtime";
import type { ReactNode } from 'react';
import { useLayoutEffect, useRef } from 'react';
import { cn } from '@/lib/ui/utils';
interface ShrinkToFitProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
  align?: 'start' | 'end';
}
interface ShrinkToFitGroupProps {
  children: ReactNode;
  className?: string;
}
export function ShrinkToFitGroup(t0) {
  const $ = _c(3);
  const {
    children,
    className
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== className) {
    t1 = <div className={className} data-shrink-to-fit-group="">{children}</div>;
    $[0] = children;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

/**
 * Preserves the requested typography until the content would overflow, then
 * scales the complete value down just enough to fit its container.
 */
export function ShrinkToFit(t0) {
  const $ = _c(14);
  const {
    children,
    className,
    contentClassName,
    align: t1
  } = t0;
  const align = t1 === undefined ? "start" : t1;
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const container = containerRef.current;
      const content = contentRef.current;
      if (container === null || content === null) {
        return;
      }
      const fitContent = () => fitShrinkToFitContent(container, content);
      fitContent();
      const frameId = requestAnimationFrame(fitContent);
      const observer = new ResizeObserver(fitContent);
      observer.observe(container);
      observer.observe(content);
      const mutationObserver = new MutationObserver(fitContent);
      mutationObserver.observe(content, {
        characterData: true,
        childList: true,
        subtree: true
      });
      let active = true;
      if (document.fonts !== undefined) {
        document.fonts.ready.then(() => {
          if (active) {
            fitContent();
          }
        });
      }
      return () => {
        active = false;
        cancelAnimationFrame(frameId);
        observer.disconnect();
        mutationObserver.disconnect();
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useLayoutEffect(t2, t3);
  const t4 = align === "end" ? "justify-end" : "justify-start";
  let t5;
  if ($[2] !== className || $[3] !== t4) {
    t5 = cn("flex min-w-0 overflow-hidden", t4, className);
    $[2] = className;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  const t6 = align === "end" ? "origin-right" : "origin-left";
  let t7;
  if ($[5] !== contentClassName || $[6] !== t6) {
    t7 = cn("w-max shrink-0 whitespace-nowrap", t6, contentClassName);
    $[5] = contentClassName;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  let t8;
  if ($[8] !== children || $[9] !== t7) {
    t8 = <div ref={contentRef} className={t7} data-shrink-to-fit-content="">{children}</div>;
    $[8] = children;
    $[9] = t7;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t5 || $[12] !== t8) {
    t9 = <div ref={containerRef} className={t5} data-shrink-to-fit="">{t8}</div>;
    $[11] = t5;
    $[12] = t8;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  return t9;
}
function fitShrinkToFitContent(container: HTMLElement, content: HTMLElement): void {
  const group = container.closest<HTMLElement>('[data-shrink-to-fit-group]');
  if (group === null) {
    applyScale([getMeasurement(container, content)]);
    return;
  }
  const measurements = Array.from(group.querySelectorAll<HTMLElement>('[data-shrink-to-fit-content]')).flatMap(groupContent => {
    if (groupContent.closest('[data-shrink-to-fit-group]') !== group) return [];
    const groupContainer = groupContent.closest<HTMLElement>('[data-shrink-to-fit]');
    return groupContainer === null ? [] : [getMeasurement(groupContainer, groupContent)];
  });
  applyScale(measurements);
}
interface ShrinkMeasurement {
  content: HTMLElement;
  scale: number;
}
function getMeasurement(container: HTMLElement, content: HTMLElement): ShrinkMeasurement {
  const availableWidth = container.clientWidth;
  content.style.removeProperty('transform');
  const contentWidth = Math.max(content.scrollWidth, content.getBoundingClientRect().width);
  const scale = availableWidth <= 0 || contentWidth <= availableWidth ? 1 : availableWidth / contentWidth;
  return {
    content,
    scale
  };
}
function applyScale(measurements: ShrinkMeasurement[]): void {
  const sharedScale = Math.min(1, ...measurements.map(({
    scale
  }) => scale));
  for (const {
    content
  } of measurements) {
    content.dataset.shrinkToFitScale = String(sharedScale);
    if (sharedScale === 1) {
      content.style.removeProperty('transform');
    } else {
      content.style.transform = `scale(${String(sharedScale)})`;
    }
  }
}